import {ReactElement, useCallback, useState, ChangeEvent} from 'react';
import {Link} from '@shopify/hydrogen';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {SignupWithEmailStepOnePayloadType} from '../../backendApi';

import {useAuthService} from '../../contexts/AuthServiceProvider.client';
import CheckboxField from '../formControls/CheckboxField.client';
import DateOfBirthField from '../formControls/DateOfBirthField.client';
import InputField from '../formControls/InputField.client';
import PhoneNumberField from '../formControls/PhoneNumberField.client';
import classNames from 'classnames';
import GrecaptchaTos from '../GrecaptchaTos';
import SocialAuthBlock from '../SocialAuthBlock.client';
import {GoogleOAuthProvider} from '@react-oauth/google';
import {config} from '../../config';

export default function SignupStep1Form(): ReactElement {
  const [formFields, setFormFields] =
    useState<SignupWithEmailStepOnePayloadType>({
      email: '',
      password: '',
      is_terms_agreed: false,
      first_name: '',
      last_name: '',
      date_of_birth: '',
      phone_number: '',
      receive_marketing: true,
      recaptcha: '',
    });

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setFormFields((prevState) => ({...prevState, [name]: value}));
  }, []);

  const handleCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const {name} = e.target;
      return setFormFields((prevState) => ({
        ...prevState,
        [name]: !prevState[name],
      }));
    },
    [],
  );

  const handlePhoneNumberChange = useCallback((phoneNumber: string) => {
    setFormFields((prevState) => ({...prevState, phone_number: phoneNumber}));
  }, []);

  const {executeRecaptcha} = useGoogleReCaptcha();

  const {handleSignupWithEmailStep1, isLoading, error} = useAuthService();

  const handleFormSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const recaptcha = await executeRecaptcha('signup');
      handleSignupWithEmailStep1({...formFields, recaptcha});
    },
    [executeRecaptcha, handleSignupWithEmailStep1, formFields],
  );

  const handleDobChange = useCallback(
    (dateStr) =>
      setFormFields((prevState) => ({
        ...prevState,
        date_of_birth: dateStr,
      })),
    [setFormFields],
  );

  const getErrorMessage = useCallback(
    (fieldName: string): string | null => {
      if (typeof error === 'object' && fieldName in error) {
        return error[fieldName];
      }
      return null;
    },
    [error],
  );

  return (
    <GoogleOAuthProvider clientId={config.googleOauth2ClientId}>
      <SocialAuthBlock action="signup" />
      <form onSubmit={handleFormSubmit}>
        {/*  .form__element-error is the error class */}
        <div className="signup__form form">
          <div className="form__row row">
            <InputField
              labelClassName="col-md-6"
              name="first_name"
              value={formFields?.first_name}
              onChange={handleChange}
              placeholder="First Name"
              error={getErrorMessage('first_name')}
              required
            />
            <InputField
              labelClassName="col-md-6"
              name="last_name"
              value={formFields?.last_name}
              onChange={handleChange}
              placeholder="Last Name"
              error={getErrorMessage('last_name')}
              required
            />
          </div>
          <div className="form__row row">
            <InputField
              labelClassName="col-md-12"
              type="email"
              name="email"
              value={formFields?.email}
              onChange={handleChange}
              placeholder="Email"
              error={getErrorMessage('email')}
              required
            />
          </div>
          <div className="form__row row">
            <InputField
              labelClassName="col-md-12"
              name="password"
              type="password"
              value={formFields?.password}
              onChange={handleChange}
              placeholder="Password"
              error={getErrorMessage('password')}
              required
            />
          </div>
          <div className="form__row row">
            <PhoneNumberField
              labelClassName="col-md-12"
              name="phone_number"
              value={formFields?.phone_number}
              onChange={handlePhoneNumberChange}
              placeholder="Mobile No."
              error={getErrorMessage('phone_number')}
            />
          </div>
          <div className="form__row row pb-10">
            <DateOfBirthField
              labelClassName="col-md-12 pt-10"
              titleClassName="signup__or"
              value={formFields.date_of_birth}
              onChange={handleDobChange}
              error={getErrorMessage('date_of_birth')}
              label="Enter your date of birth"
              required
            />
          </div>
          <div className="form__row row signup__terms">
            <div className="col-md-12 signup__term">
              <CheckboxField
                name="receive_marketing"
                checked={formFields?.receive_marketing}
                onChange={handleCheckboxChange}
                error={getErrorMessage('receive_marketing')}
              >
                Would you like to receive announcements and exclusive offers?
              </CheckboxField>
            </div>
            <div className="col-md-12 signup__term">
              <CheckboxField
                name="is_terms_agreed"
                checked={formFields?.is_terms_agreed}
                onChange={handleCheckboxChange}
                error={getErrorMessage('is_terms_agreed')}
              >
                By continuing you agree to our&nbsp;
                <Link to="/terms" target="_blank">
                  Term &amp; Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank">
                  Privacy Policy
                </Link>
              </CheckboxField>
            </div>
          </div>
          <div className="form__row row signup__submit">
            <div
              className={classNames('col-md-12', {
                'error-msg': getErrorMessage('recaptcha'),
              })}
            >
              {getErrorMessage('recaptcha')}
            </div>
            <div className="col-md-12">
              <button
                type="submit"
                className="button button_form button_full button_size_large"
                disabled={isLoading}
              >
                Create account
              </button>
            </div>
            <GrecaptchaTos className="mt-10" />
          </div>
          <div className="form__row row signup__already">
            <div className="col-md-12">
              Already have a Whisky Lover account?&nbsp;
              <Link to="/login" className="u-hover">
                Login here
              </Link>
            </div>
          </div>
        </div>
      </form>
    </GoogleOAuthProvider>
  );
}
